import { useCallback, useState, useMemo } from "react"

import ExcelJS from 'exceljs'

import { useApiAccess } from 'features/session/context'
import { useSearch } from 'features/search/context'
import { usePrint } from 'routes/context/printContext'

import { KojinResult } from "interface/kojin"
import { KojinListDownloadParams } from 'shared/indexedDb'

import { Stack } from '@mui/material'
import { KojinPrintFormatList } from 'features/kojin/report/print'
import { KojinPptxFormatList } from 'features/kojin/report/pptx'
import { ColumnDefs } from '../constants'

type useKojinListDownloadProps = {
	list: KojinResult[] | null
}
export const useKojinListDownload = ({
	list
}: useKojinListDownloadProps) => {
  const apiAccess = useApiAccess()
	const search = useSearch()
  const print = usePrint()

	const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false)
	const [downloading, setDownloading] = useState<boolean>(false)
	const [downloadProgress, setDownloadProgress] = useState<number | null>(null)
  
	const [downloadSummary, setDownloadSummary] = useState<{
    targetCount: number
    extendCount: number
  } | null>(null)

  const download = useCallback(
		async (
			title: string,
			params: KojinListDownloadParams,
			from: number,
			to: number,
			onProgress: (percent: number) => void
		): Promise<boolean> => {
			const targetList = (list ?? [])
				.filter((kojin) => {
					if (kojin.target?.sourceType === 1) {
						if ((kojin.annotate?.score ?? 0) > 0) return false
						if (params.target === 'target') return false
					} else {
						if (params.target === 'extend') return false
					}
					return true
				})
				.slice(from, to)
			if (targetList.length === 0) return false

			if (params.format === 'report') {
				if (!params.reportId || !search || !print) return false

				const formatter = KojinPrintFormatList.find(
					(r) => r.reportId === params.reportId
				)
				if (!formatter) return false
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const preProcessData: any[] = []
				for (const target of targetList) {
					const reportData = await formatter.preProcess({ apiAccess, search, kojin: target })
					if (reportData) {
						preProcessData.push(reportData)
					}
				}
				print.doPrint(
					<Stack direction="column" spacing={1}>
						{targetList.map((kojin, i) => (
							<formatter.record
								key={`print-record-${i}`}
								search={search}
								kojin={kojin}
								preProcessData={preProcessData}
								counter={i}
								count={targetList.length}
							/>
						))}
					</Stack>
				)
			} else if (params.format === 'pptx') {
				if (!params.reportId || !search) return false

				const formatter = KojinPptxFormatList.find((r) => r.reportId === params.reportId)
				if (!formatter) return false

				await formatter.record(title, {
					apiAccess,
					search,
					kojinList: targetList
				}, onProgress)
			} else {
				const workbook = new ExcelJS.Workbook()
				workbook.addWorksheet('sheet1')
				const worksheet = workbook.getWorksheet('sheet1')
				if (!worksheet) return false

				const column = params.columns.flatMap((col) => ColumnDefs[col] ?? [])
				worksheet.columns = column.map((col) => ({ header: col.label }))
				worksheet.addRows(targetList.map((kojin) => column.map((col) => col.val(kojin))))

				const uint8Array =
					params.format === 'csv' ? await workbook.csv.writeBuffer() :
					params.format === 'tsv' ? await workbook.csv.writeBuffer({
						formatterOptions: {
							delimiter: '\t',
							quote: false,
						}
					}) : await workbook.xlsx.writeBuffer()
				const blob = new Blob([uint8Array], { type: 'application/octet-binary' })
				const url = window.URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = title + (
					params.format === 'csv' ? '.csv' :
					params.format === 'tsv' ? '.tsv' : '.xlsx'
				)
				a.click()
				a.remove()
			}
			return true
		},
		[list, apiAccess, print, search]
	)

	const openDownloadDialog = useCallback(
		() => {
			if (!list || showDownloadDialog) return

			setDownloadSummary(list.reduce(
				(s, kojin) => {
					if (kojin.target?.sourceType === 1) {
						if ((kojin.annotate?.score ?? 0) > 0) return s
						return { ...s, extendCount: s.extendCount + 1 }
					} else {
						return { ...s, targetCount: s.targetCount + 1 }
					}
				}, {
					extendCount: 0,
					targetCount: 0
				}
			))
			setShowDownloadDialog(true)
		},
		[setShowDownloadDialog, list, showDownloadDialog]
	)
	const closeDownloadDialog = useCallback(
		() => setShowDownloadDialog(false),
		[setShowDownloadDialog]
	)
	const doDownload = useCallback(
		async (
			title: string,
			params: KojinListDownloadParams,
			from: number,
			to: number
		) => {
			setDownloading(true)
			setDownloadProgress(null)
			await download(title, params, from, to, (percent) => setDownloadProgress(percent))
			setDownloading(false)
			setDownloadProgress(null)
			closeDownloadDialog()
		},
		[download, setDownloading, closeDownloadDialog, setDownloadProgress]
	)

	return useMemo(() => ({
		openDownloadDialog,
		closeDownloadDialog,
		showDownloadDialog,
		downloading,
		downloadProgress,
		downloadSummary,
		doDownload
	}), [
		openDownloadDialog,
		closeDownloadDialog,
		showDownloadDialog,
		downloading,
		downloadProgress,
		downloadSummary,
		doDownload
	])
}